import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LocoFeature } from '../../loco/shared/model/loco-feature.model';
import { AllocationStation } from '../model/allocation-station.model';
import { IntendedUsageVO } from '../model/intended-usage-vo';
import { LocoDetail } from '../model/loco-detail.model';
import { MessageDetails } from '../model/message-details.model';
import { TerminalServiceCenter } from '../model/terminal-service-center.model';
import { StaticDataStoreService } from '../services/static-datastore.service';
import { environment } from './../../../environments/environment';
import { ReasonVO } from './../model/reason-vo';
import { Tab } from './../model/tab.model';
import { TerminalCommonInfo } from './../model/terminal-common-info.model';
import { ConfigService } from './../services/config.service';
import { LmsLoaderService } from './lms-loader.service';
import { LmsRendererService } from './lms-renderer.service';
import { LmsTerminalStatusSubmenuService } from './lms-terminal-status-submenu.service';
import { MessageHandlerService } from './message-handler.service';
import { TabService } from './tab.service';


/**
 * @export
 * @class AppUtilityService
 */
@Injectable()
export class AppUtilityService {

  commonopsPicklist: any;
  filteredResults: Array<string> = [];
  lmsAutoComplete: any;
  zoneList: any;
  configData: any;
  windowHeight: number;
  windowWidth: number;

  // hideAlertCount = false;

  private screenresize: Subject<any> = new Subject();
  screenResize$ = this.screenresize.asObservable();
  allocationStationArr: Array<AllocationStation> = [];

  /**
   * Creates an instance of AppUtilityService.
   * @param {StaticDataStoreService} staticDataStoreService
   * @param {LmsLoaderService} lmsLoaderService
   * @param {LmsTerminalStatusSubmenuService} lmsTerminalStatusSubmenuService
   *
   * @memberOf AppUtilityService
   */
  constructor(private staticDataStoreService: StaticDataStoreService,
    private lmsLoaderService: LmsLoaderService,
    private lmsTerminalStatusSubmenuService: LmsTerminalStatusSubmenuService,
    private configService: ConfigService,
    private lmsRendererService: LmsRendererService,
    private router: Router,
    private tabService: TabService,
    private messageHandlerService: MessageHandlerService) {
    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    this.configData = this.configService.getConfigData();
    this.lmsRendererService.resizeObervable.pipe(debounceTime(10)).subscribe((isResize) => {
      if (isResize) {
        this.windowHeight = this.lmsRendererService.getWindowHeight();
        this.windowWidth = this.lmsRendererService.getWindowWidth();
        this.setSideMenuHeight();
      }
    });
  }


  updateScreenResize() {
    this.screenresize.next(Math.random());
  }

  /**
   * formmating of locomotive id for send it to service call.
   * @param {string} value
   * @returns {string}
   * @memberOf AppUtilityService
   */
  formatLocomotiveId(value: string): string {
    let formattedValue = value;
    if (value) {
      value = value.replace(/\s/g, '');
      if (this.isNumeric(value.charAt(0))) {
        formattedValue = 'CSXT' + '    ' + this.leftPad(value, '0', 6);
      } else {
        const v = value.match(/\d+/);
        if (v) {
          const locoInitial = (v.index <= 4) ? value.substring(0, v.index) : value.substring(0, 4);
          const locoNumber = (v.index <= 4) ?
            value.substring(v.index, value.length) : value.substring(4, value.length);

          if (this.isNumeric(locoNumber)) {
            formattedValue = this.rightPad(locoInitial, ' ', 4) + '    ' + this.leftPad(locoNumber, '0', 6);
          }
        }
      }
    }
    return formattedValue;
  }

  /**
    * Check If Terminal Mnemonic is valid or not
    * @param {string} value
    * @returns {boolean}
    * @memberOf LmsLocoTopActionsComponent
    */
  isMnemonicExist(value: string): boolean {
    if (value) {
      value = value.toUpperCase();
      if (value === 'DELETE' || value === 'OFFLN' || value === 'NEW' || value === 'PARKED') {
        return true;
      }
      if (this.staticDataStoreService.getValue('commonopsPicklist')) {
        const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
        if (commonopsPicklist) {
          const data = commonopsPicklist.mapOfMnemonis ? commonopsPicklist.mapOfMnemonis : null;
          for (const key in data) {
            if (data.hasOwnProperty(key) && value.trim() === (data[key]).trim()) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  /**
   * Check if locomotive id is valid or not
   * @param {string} value
   * @returns {boolean}
   * @memberOf AppUtilityService
   */
  isValidLocomotiveId(value: string): boolean {
    if (value) {
      value = value.replace(/\s/g, '');
      const v = value.match(/\d+/);
      if (v) {
        const letter = value.substring(0, v.index).match(/^[A-Za-z]+$/);
        if (letter &&
          value.substring(0, v.index).length >= 2 && value.substring(0, v.index).length <= 4
          && value.length <= 14) {
          const locoNumber = (v.index <= 4) ?
            value.substring(v.index, value.length) : value.substring(4, value.length);
          if (locoNumber && locoNumber.length <= 6 && this.isNumeric(locoNumber)) {
            return true;
          } else {
            return false;
          }
        }
      }
    }
  }

  /**
   * Format Loco Id for UI SCREEN
   * @private
   * @param {string} value
   * @returns
   * @memberOf AppUtilityService
   */
  UIformatLocomotiveId(value: string) {
    let formattedValue = value;
    if (value) {
      value = value.replace(/\s/g, '');
      if (this.isNumeric(value.charAt(0))) {
        formattedValue = 'CSXT' + ' ' + value.replace(/^0+/, '');
      } else {
        const v = value.match(/\d+/);
        if (v) {
          const locoInitial = (v.index <= 4) ? value.substring(0, v.index) : value.substring(0, 4);
          const locoNumber = (v.index <= 4) ?
            value.substring(v.index, value.length) : value.substring(4, value.length);

          if (this.isNumeric(locoNumber)) {
            formattedValue = locoInitial + ' ' + locoNumber.replace(/^0+/, '');
          }
        }
      }
    }
    return formattedValue.toUpperCase();
  }

  /**
   * Get list of particular type of locomotives.
   *
   * @param {any} locos
   * @returns
   *
   * @memberOf AppUtilityService
   */
  getLocomotiveList(locos) {
    const otherLocos = [];

    for (let i = 0; i < locos.length; i++) {
      const loco = locos[i];
      if (!_.isUndefined(loco.locomotiveIdentifier) && loco.locomotiveIdentifier.toLowerCase() !== 'zzzzzzzzzzzzzz') {
        const locomotive = {
          rowID: '',
          locomotiveID: this.formatLocomotiveId(loco.locomotiveIdentifier),
          assignedPlannedFlag: loco.assignedPlannedFlag
        };
        otherLocos.push(locomotive);
      }
    }
    return otherLocos;
  }

  /**
   * @param {any} str
   * @param {any} padString
   * @param {any} max
   * @returns {string}
   *
   * @memberOf AppUtilityService
   */
  leftPad(str, padString, max): string {
    if (str) {
      str = str.toString();
      return str.length < max ? this.leftPad(padString + str, padString, max) : str;
    }
  }

  leftPadWithZero(str, padString, max): string {
    str = str.toString();
    return this.leftPad(str, padString, max);
  }
  /**
   * @param {any} str
   * @param {any} padString
   * @param {any} max
   * @returns {string}
   *
   * @memberOf AppUtilityService
   */
  rightPad(str, padString, max): string {
    if (str) {
      str = str.toString();
      return str.length < max ? this.rightPad(str + padString, padString, max) : str;
    }
  }

  /**
    * Enable only numeric value
    * @param {any} evt
    * @returns {boolean}
    * @memberOf LmsLocoTopActionsComponent
    */
  isNumber(evt): boolean {
    evt = (evt) ? evt : window.event;
    const charCode = (evt.which) ? evt.which : evt.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  /**
    * Enable only numeric value
    * @param {any} evt
    * @returns {boolean}
    * @memberOf LmsLocoTopActionsComponent
    */
  isNegativePositiveNumber(evt): boolean {
    // not allow - after any value -- start
    evt = (evt) ? evt : window.event;
    const charCode = (evt.which) ? evt.which : evt.keyCode;

    if (charCode === 45) {
      return true;
    }
    // not allow - after any value -- end


    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }
  /**
  * Enable only numeric value
  * @param {string} charCode
  * @returns {boolean}
  * @memberOf LmsLocoTopActionsComponent
  */
  isValidNumber(charCode: string): boolean {
    const rg = /^-?[0-9]\d*$/;
    return rg.test(charCode);

  }
  /**
     * Check for train id.
     *
     * @param {string} value
     * @returns {boolean}
     *
     * @memberOf LmsTabsComponent
     */
  isValidTrainId(value: string): boolean {
    let flag = false;
    if (value && value.length === 6) {
      if (!this.isNumeric(value.charAt(0))) {
        const remainingStr = value.substring(1, value.length);
        if (this.isNumeric(remainingStr)) {
          flag = true;
        }
      }
    }
    return flag;
  }

  /**
     * Check for train id.
     *
     * @param {string} value
     * @returns {boolean}
     *
     * @memberOf LmsTabsComponent
     */
  isValidProfileTrainId(value: string): boolean {
    let flag = false;
    if (value && (value.length === 6 || value.length === 4)) {
      if (!this.isNumeric(value.charAt(0))) {
        const remainingStr = value.substring(1, value.length);
        if (this.isNumeric(remainingStr)) {
          flag = true;
        }
      }
    }
    return flag;
  }

  /**
  * To check for numeric value.
  * @private
  * @param {string} value
  * @returns {boolean}
  * @memberOf LmsTerminalHandlingComponent
  */
  isNumeric(value: string): boolean {
    if (value) {
      const v = value.match(/^\d+$/);
      if (v) {
        if (v.toString() === value) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Remove space from locomotive id.
   * @param {string} value
   * @returns {string}
   *
   * @memberOf AppUtilityService
   */
  removeSpaceLocomotiveId(value: string): string {
    if (value) {
      value = value.trim();
      const v = value.match(/\d+/);
      if (v) {
        const locoInitial = (v.index <= 4) ? value.substring(0, v.index).trim() : value.substring(0, 4).trim();
        const locoNumber = (v.index <= 4) ? value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();
        return locoInitial + ' ' + locoNumber.replace(/^0+/, '');
      }
    }
  }

  /**
   * Formatting of locomotive id for display.
   *
   * @param {string} value
   * @returns {string}
   *
   * @memberOf AppUtilityService
   */
  formatLocomotiveIdForDispaly(value: string): string {
    if (value) {
      value = value.trim();
      const v = value.match(/\d+/);
      if (v) {
        const locoInitial = (v.index <= 4) ? value.substring(0, v.index).trim() : value.substring(0, 4).trim();
        const locoNumber = (v.index <= 4) ? value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();
        return locoInitial + ' ' + locoNumber.replace(/^0+/, '');
      }
    }
  }

  /**
   * Prepare locomotive feature list.
   * @param {LocoDetail[]} locomotives
   * @returns {LocoDetail[]}
   *
   * @memberOf AppUtilityService
   */
  public prepareLocomotiveFeatureList(locomotives: LocoDetail[]): LocoDetail[] {
    const newList: LocoDetail[] = [];
    if (locomotives && locomotives.length > 0) {
      locomotives.forEach((locomotive, index) => {
        const loco = this.getFeaturesFromPicklist(locomotive);
        loco.originalSequence = index + 1;
        newList.push(loco);
      });
    }
    return newList;
  }

  /**
   * this method returns the desk name of
   * given desk id
   * @param {*} deskId
   * @returns
   * @memberof AppUtilityService
   */
  public getDeskNameById(deskId: any) {
    let deskName = '';
    if (!this.zoneList || this.zoneList.length < 1) {
      if (this.configData && this.configData.deskOptions && this.configData.deskOptions.values) {
        this.zoneList = this.configData.deskOptions.values;
      } else {
        this.zoneList = [];
      }
    }
    this.zoneList.forEach((zone) => {
      if (zone.value === deskId) {
        deskName = zone.label;
        return deskName;
      }
    });
    return (deskName && deskName.length > 0) ? deskName : deskId;
  }

  /**
   * Get features from picklist service.
   *
   * @private
   * @param {LocoDetail} locomotive
   * @returns {LocoDetail}
   *
   * @memberOf AppUtilityService
   */
  public getFeaturesFromPicklist(locomotive: LocoDetail): LocoDetail {
    const newFeatureList: LocoFeature[] = [];
    let locoFeatureListArr: any;
    if (!this.commonopsPicklist) {
      this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    }
    if (this.commonopsPicklist && this.commonopsPicklist.masterData && this.commonopsPicklist.masterData.locoFeaturesList) {
      locoFeatureListArr = this.commonopsPicklist.masterData.locoFeaturesList;
    }
    if (locomotive && locomotive.listOfFeature && locomotive.listOfFeature.length > 0) {
      locomotive.listOfFeature.forEach(feature => {
        feature.featureDescription = this.getFeatureValueFromCode(feature.featureDescription, locoFeatureListArr);
        if (feature && feature.featureDescription && feature.featureDescription.length > 0) {
          newFeatureList.push(feature);
        }
      });
    }
    locomotive.listOfFeature = newFeatureList;

    return locomotive;
  }

  /**
   * Get feature value from code.
   * @private
   * @param {string} featureCode
   * @param {*} locoFeatureListArr
   * @returns {string}
   *
   * @memberOf AppUtilityService
   */
  public getFeatureValueFromCode(featureCode: string, locoFeatureListArr: any): string {
    let description = '';
    if (locoFeatureListArr && locoFeatureListArr.length > 0) {
      const newFeature = locoFeatureListArr.filter((feature) => { return feature.featureCode === featureCode || feature.featureDescription === featureCode; });
      if (newFeature && newFeature.length > 0) {
        description = newFeature[0].featureDescription;
      }
    }
    return description;
  }

  /**
   * @param {string[]} value
   * @returns
   *
   * @memberOf AppUtilityService
   */
  checkMultipleBlankStatus(value: string[]) {
    let isBlank: boolean;
    for (let i = 0; i < value.length; i++) {
      if (value[i]) {
        isBlank = false;
        break;
      } else {
        isBlank = true;
      }
    }
    return isBlank;
  }

  /**
   * Check for loco is assigned or planned.
   * @param {LocoDetail} loco
   * @returns {boolean}
   * @memberOf AppUtilityService
   */
  isPlannedLoco(loco: LocoDetail): boolean {
    let flag: boolean;
    if (loco) {
      if (loco.assignedPlannedFlag && loco.assignedPlannedFlag.trim().toUpperCase() === 'P') {
        flag = true;
      } else if ((loco.plannedAssignment && loco.plannedAssignment.trim().length > 0) || (loco.plannedOrCurrentAssign && loco.plannedOrCurrentAssign.trim().length > 0)) {
        flag = true;
      }
    }
    return flag;
  }

  /**
   * Remove Leading zero from value
   * @param {string} value
   * @returns
   * @memberof AppUtilityService
   */
  removeLeadingZero(value: string) {
    if (value && '000' !== value) {
      return parseInt(value, 10);
    } else {
      return '';
    }
  }


  /**
   * Returns Desk Name
   * @param {Tab} tab
   * @returns {string}
   * @memberof AppUtilityService
   */
  showDeskName(tab: Tab): string {
    let managerDesk: any;
    managerDesk = this.staticDataStoreService.getValue('commonopsPicklist');
    if (tab && tab.tabSearchParameter) {
      if (tab.tabSearchParameter.searchText === '') {
        return '';
      } else {
        if (managerDesk && managerDesk.masterData) {
          for (let i = 0; i < managerDesk.masterData.zoneList.length; i++) {
            if (managerDesk.masterData.zoneList[i].zoneCode === tab.tabSearchParameter.searchText) {
              return managerDesk.masterData.zoneList[i].zoneCode + ' ' + managerDesk.masterData.zoneList[i].zoneDescription;
            }
          }
        }
      }
    } else {
      return '';
    }
  }

  public removeFilterTextBoxValue() {
    const filterBox = document.querySelector('.spinner-values .p-dropdown-panel .p-dropdown-filter-container .p-dropdown-filter');
    if (filterBox) {
      filterBox['value'] = '';
    }
  }

  /**
   * Validate Common Picklist data.
   * @param {*} commonopsPicklist
   * @returns {*}
   * @memberof AppUtilityService
   */
  public validateCommonPickList(commonopsPicklist: any): any {
    let commonopsPicklistData: any;
    if (!commonopsPicklist || (this.commonopsPicklist && !this.commonopsPicklist.masterData)
      || (this.commonopsPicklist && !this.commonopsPicklist.mapOfMnemonis)
      || (this.commonopsPicklist && !this.commonopsPicklist.setOfTerminals)) {
      commonopsPicklistData = this.staticDataStoreService.getValue('commonopsPicklist');
    } else {
      commonopsPicklistData = commonopsPicklist;
    }
    return commonopsPicklistData;
  }

  public getRowPerPageCount(rowHeight: number, upperHeight: number): number {
    let rowCount = 0;
    if (rowHeight > 0 && upperHeight > 0) {
      const availableHeight = this.windowHeight - upperHeight;
      rowCount = Math.ceil(availableHeight / rowHeight) + 5;
    }
    return rowCount;
  }

  private setSideMenuHeight() {
    const windowHeight = window.innerHeight;
    let heightToAdjust = 0;
    if (window.innerWidth < 1280) {
      heightToAdjust = 22;
    }
    const leftMenuItems = document.getElementById('leftMenuItems');
    if (windowHeight && leftMenuItems) {
      leftMenuItems.removeAttribute('style');
      leftMenuItems.style.maxHeight = (windowHeight) + 'px';
      leftMenuItems.style.minHeight = ' ' + (windowHeight - 36 - heightToAdjust) + 'px';
    }
  }

  /**
   * Check for valid terminal handling plan selection.
   * @param {string} terminalMnemonic
   * @returns {boolean}
   * @memberof AppUtilityService
   */
  isValidThpSelection(terminalMnemonic: string): boolean {
    if (terminalMnemonic) {
      const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
      if (commonopsPicklist && commonopsPicklist.masterData && commonopsPicklist.masterData.serviceCentreList) {
        for (let i = 0; i < commonopsPicklist.masterData.serviceCentreList.length; i++) {
          const terminalSvcCtr: TerminalServiceCenter = commonopsPicklist.masterData.serviceCentreList[i];
          if (terminalSvcCtr && (terminalSvcCtr.terminalMnemonic.trim().toUpperCase() === terminalMnemonic.trim().toUpperCase())
            && (terminalSvcCtr.hasServiceCentre.trim().toUpperCase() === 'Y')) {
            return true;
          }
        }
      }
    }
    return false;
  }

  trimUpperCase(str) {
    if (str) {
      return str.trim().toUpperCase();
    }
    return '';
  }
  navigateByTabId(url: string) {
    // this.router.
    //   this.router.navigateByUrl(url);
  }

  /**
   * Locate to tab as per type and sub type.
   * @param {string} type
   * @param {string} subType
   * @param {string} searchText
   * @param {boolean} isLocate
   * @memberof AppUtilityService
   */
  public navigateByUrl(type: string, subType: string, searchText: string, isLocate: boolean, isUrlById?: boolean, idForTab?: any) {
    if (isUrlById && idForTab) {
      this.router.navigateByUrl(
        `${type.toLowerCase()}/${subType.toLowerCase()}/${idForTab}`,
        { skipLocationChange: environment.hideUrl });
    } else {
      if (this.tabService.isTabRedirectionAllowed(type, subType)) {
        if (searchText) {
          this.router.navigateByUrl(
            `${type.toLowerCase()}/${subType.toLowerCase()}/${new Date().getTime()}?search=${searchText}`,
            { skipLocationChange: environment.hideUrl });
        } else {
          this.router.navigateByUrl(
            `${type.toLowerCase()}/${subType.toLowerCase()}/${new Date().getTime()}`,
            { skipLocationChange: environment.hideUrl });
        }
      } else {
        this.messageHandlerService.handleMessage(<MessageDetails>({
          type: this.configService.getConfigData().message.type.client,
          level: this.configService.getConfigData().message.level.error,
          target: this.configService.getConfigData().message.target.page,
          message: this.configService.getConfigData().messages.maxTab
        }));
      }
    }
  }

  public locate(type: string, subType: string, searchText: string, locoteLocoId: string) {
    if (this.tabService.isTabRedirectionAllowed(type, subType)) {
      if (searchText) {
        this.router.navigateByUrl(
          `${type.toLowerCase()}/${subType.toLowerCase()}/${new Date().getTime()}?search=${searchText}&locateLocoId=${locoteLocoId}`,
          { skipLocationChange: environment.hideUrl });
      }
    } else {
      this.messageHandlerService.handleMessage(<MessageDetails>({
        type: this.configService.getConfigData().message.type.client,
        level: this.configService.getConfigData().message.level.error,
        target: this.configService.getConfigData().message.target.page,
        message: this.configService.getConfigData().messages.maxTab
      }));
    }
  }

  /**
     * Check for train id.
     *
     * @param {string} value
     * @returns {boolean}
     *
     * @memberOf LmsTabsComponent
     */
  isValidTerminalId(value: string): boolean {
    let flag = false;
    if (value) {
      if (this.isAlphabets(value)) {
        flag = true;
      }
    }
    return flag;
  }

  isAlphabets(value: string): boolean {
    if (value) {
      const v = value.match(/^[a-zA-Z]+$/);
      if (v) {
        if (v.toString() === value) {
          return true;
        }
      }
    }
    return false;
  }
  public setFocusToGivenElement(inputId: string, timeOut?: string) {
    if (inputId && inputId.length > 0) {
      if (!timeOut) {
        const input = document.getElementById(inputId);
        if (input) {
          setTimeout(() => {
            input.focus();
          }, 20);
        }
      }
      if (timeOut) {
        setTimeout(() => {
          const input = document.getElementById(inputId);
          if (input) {
            input.focus();
          }
        }, 20);
      }
    }
  }
  public setFocusToFirstElement(id: string) {
    if (id && id.length > 0) {
      const element = document.getElementById(id);
      if (element) {
        let focusedElem;
        const inputElements = element.querySelectorAll('input');
        let inputFound = false;
        if (inputElements && inputElements.length > 0) {
          for (let i = 0; i < inputElements.length; i++) {
            if (inputElements[i] && !inputElements[i].disabled) {
              focusedElem = inputElements[i];
              break;
            }
          }
        }
        if (focusedElem) {
          inputFound = true;
          focusedElem.focus();
        }
        if (!inputFound) {
          setTimeout(() => {
            const buttonElements = element.querySelectorAll('button');
            if (buttonElements && buttonElements.length > 0) {
              for (let i = 0; i < buttonElements.length; i++) {
                if (buttonElements[i] && !buttonElements[i].disabled && buttonElements[i].classList.contains('p-button-primary')) {
                  focusedElem = buttonElements[i];
                  break;
                }
              }
            }
            if (focusedElem) {
              inputFound = true;
              focusedElem.focus();
            }
          }, 50);
        }
      }
    }
  }

  public getHttpErroMessage(error: any, isAfterSuccess: Boolean) {
    if (error && !error.ok) {
      if (error._body) {
        if (typeof (error._body) === 'string') {
          if (error.headers.get('content-type').includes('text/html')) {
            if (error.status === 500) {
              return this.configData.messages.sessionExpiredError;
            } else if (error.statusText) {
              return error.statusText;
            } else {
              return this.configData.messages.internalServerError;
            }
          } else {
            return error._body;
          }
        } else {
          return this.configData.messages.offlineError;
        }
      } else if (error.statusText) {
        return error.statusText;
      } else {
        return this.configData.messages.internalServerError;
      }
    }
    return '';
  }

  /**
   *
   * fetch city/state from terminal mnemonic.
   * @param {string} terminalMnemonic
   * @returns {string}
   * @memberof AppUtilityService
   */
  getTerminalCityState(terminalMnemonic: string): string {
    let terminalCommonInfoArr: Array<TerminalCommonInfo> = [];
    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (this.commonopsPicklist && this.commonopsPicklist.setOfTerminals) {
      terminalCommonInfoArr = this.commonopsPicklist.setOfTerminals;
    }
    if (terminalCommonInfoArr && terminalMnemonic) {
      for (let i = 0; i < terminalCommonInfoArr.length; i++) {
        const terminalCommonInfo: TerminalCommonInfo = terminalCommonInfoArr[i];
        if (terminalCommonInfo && terminalCommonInfo.terminalMnemonic
          && (terminalCommonInfo.terminalMnemonic.trim().toUpperCase() === terminalMnemonic.trim().toUpperCase())) {
          const terminalCity = terminalCommonInfo.city.trim();
          const terminalState = terminalCommonInfo.stateCode.trim();
          return terminalCity + ', ' + terminalState;
        }
      }
    }
    return '-';
  }

  autoCompleteForTerminal(event) {
    const mnemonics = [];
    const mileposts = [];
    this.commonopsPicklist = this.validateCommonPickList(this.commonopsPicklist);
    if (this.commonopsPicklist && this.commonopsPicklist.mapOfMnemonis) {
      const data = this.commonopsPicklist.mapOfMnemonis ? this.commonopsPicklist.mapOfMnemonis : null;
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          mnemonics.push(data[key]);
          mileposts.push(key);
        }
      }
    }
    this.filteredResults = mnemonics.filter((s) => {
      return s.startsWith(event.query.toUpperCase());
    });

    Array.prototype.push.apply(this.filteredResults, (mileposts.filter((s) => {
      return s.startsWith(event.query.toUpperCase());
    })));

    this.filteredResults = this.filteredResults.filter(function (item, index, inputArray) {
      return inputArray.indexOf(item) === index;
    });
    return this.filteredResults.sort();
  }

  getValueForMnemonicMilepost(value: string) {
    let emitValue = value;
    const commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (commonopsPicklist) {
      const data = commonopsPicklist.mapOfMnemonis ? commonopsPicklist.mapOfMnemonis : null;
      if (data) {
        if (data[value]) {
          emitValue = data[value];
        }
      }
    }
    return emitValue;
  }

  isValidAllocationStation(divison: string, stationName: string) {
    const allocStationList = this.staticDataStoreService.getValue('commonopsPicklist').masterData.allocationStationList;
    if (allocStationList && allocStationList.length > 0) {
      const stationArr = _.filter(allocStationList,
        { 'divisionMnemonic': divison, 'allocationStation': stationName });
      if (stationArr && stationArr.length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get terminal common info object by terminal menmonic.
   * @param {string} terminalMnemonic
   * @returns {TerminalCommonInfo}
   * @memberof AppUtilityService
   */
  getTerminalInfoObject(terminalMnemonic: string): TerminalCommonInfo {
    let terminalCommonInfo: TerminalCommonInfo;
    let terminalCommonInfoArr: Array<TerminalCommonInfo> = [];
    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (this.commonopsPicklist && this.commonopsPicklist.setOfTerminals) {
      terminalCommonInfoArr = this.commonopsPicklist.setOfTerminals;
    }
    if (terminalCommonInfoArr && terminalMnemonic) {
      for (let i = 0; i < terminalCommonInfoArr.length; i++) {
        if (terminalCommonInfoArr[i] && terminalCommonInfoArr[i].terminalMnemonic
          && (terminalCommonInfoArr[i].terminalMnemonic.trim().toUpperCase() === terminalMnemonic.trim().toUpperCase())) {
          terminalCommonInfo = terminalCommonInfoArr[i];
        }
      }
    }
    return terminalCommonInfo;
  }

  /**
   * Get Intended Usage description from code.
   *
   * @memberof AppUtilityService
   */
  getIntendedUsageDesc(value: string) {
    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (value && this.commonopsPicklist && this.commonopsPicklist.intendedUsageDataVO && this.commonopsPicklist.intendedUsageDataVO.intendedUsageVoList) {
      for (let i = 0; i < this.commonopsPicklist.intendedUsageDataVO.intendedUsageVoList.length; i++) {
        const intendedUsageVO: IntendedUsageVO = this.commonopsPicklist.intendedUsageDataVO.intendedUsageVoList[i];
        if (intendedUsageVO && (intendedUsageVO.intendedUsageC.trim().toUpperCase() === value.trim().toUpperCase())) {
          return intendedUsageVO.intendedUsageLongX;
        }
      }
    }
    return '';
  }

  /**
   * Get Intended Usage short description from code.
   *
   * @memberof AppUtilityService
   */
  getIntendedUsageShortDesc(value: string) {
    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (value && this.commonopsPicklist && this.commonopsPicklist.intendedUsageDataVO && this.commonopsPicklist.intendedUsageDataVO.intendedUsageVoList) {
      for (let i = 0; i < this.commonopsPicklist.intendedUsageDataVO.intendedUsageVoList.length; i++) {
        const intendedUsageVO: IntendedUsageVO = this.commonopsPicklist.intendedUsageDataVO.intendedUsageVoList[i];
        if (intendedUsageVO && intendedUsageVO.intendedUsageC && (intendedUsageVO.intendedUsageC.trim().toUpperCase() !== 'HE') && (intendedUsageVO.intendedUsageC.trim().toUpperCase() === value.trim().toUpperCase())) {
          return intendedUsageVO.intendedUsageShortX;
        }
      }
    }
    return '';
  }

  /**
   * Get Reason description from reason code.
   *
   * @memberof AppUtilityService
   */
  getIntendedUsageReasonDesc(value: string) {
    this.commonopsPicklist = this.staticDataStoreService.getValue('commonopsPicklist');
    if (value && this.commonopsPicklist && this.commonopsPicklist.intendedUsageDataVO && this.commonopsPicklist.intendedUsageDataVO.reasonVoList) {
      for (let i = 0; i < this.commonopsPicklist.intendedUsageDataVO.reasonVoList.length; i++) {
        const reasonVO: ReasonVO = this.commonopsPicklist.intendedUsageDataVO.reasonVoList[i];
        if (reasonVO && (reasonVO.usageReasonC.trim().toUpperCase() === value.trim().toUpperCase())) {
          if (reasonVO.usageReasonX && reasonVO.usageReasonX.trim().toUpperCase() === 'BLANK') {
            return 'No reason required';
          } else {
            return reasonVO.usageReasonX;
          }
        }
      }
    }
    return '';
  }

  getFormattedIncidentLocoId(value: string): string {
    if (value) {
      value = this.formatLocomotiveId(value);
      if (value) {
        value = value.replace(/\s/g, '');
      }
      return value;
    }
  }

  getHelpUrl(type: string, subType: string) {
    let url = 'https://csx.atlassian.net/wiki/display/HC';
    if (type && type.length > 0 && subType && subType.length > 0) {
      switch (type) {
        case 'Terminal':
          if ('STATUS' === subType.toUpperCase()) {
            url = url + '/Terminal+Status';
          } else if ('HANDLING' === subType.toUpperCase()) {
            url = url + '/Terminal+Handling+Plan';
          } else if ('NOTES' === subType.toUpperCase()) {
            url = url + '/Terminal+Notes';
          }
          break;
        case 'Locomotive':
          if ('DATA' === subType.toUpperCase()) {
            url = url + '/Locomotive+Data';
          } else if ('HISTORY' === subType.toUpperCase()) {
            url = url + '/Locomotive+History';
          } else if ('NOTES' === subType.toUpperCase()) {
            url = url + '/Locomotive+Notes';
          }
          break;
        case 'Train':
          if ('DATA' === subType.toUpperCase()) {
            url = url + '/Train+Data';
          } else if ('PROFILE' === subType.toUpperCase()) {
            url = url + '/Train+Profile';
          } else if ('HISTORY' === subType.toUpperCase()) {
            url = url + '/Train+History';
          } else if ('NOTES' === subType.toUpperCase()) {
            url = url + '/Train+Notes';
          }
          break;
        case 'Alerts':
          url = url + '/Alerts';
          break;
        case 'Locomotive-Status':
          url = url + '/Locomotive+Status';
          break;
        case 'Yard-Local':
          url = url + '/Yard+and+Local';
          break;
        case 'Outlying-Points':
          url = url + '/Outlying+Points';
          break;
        case 'Query-View':
          url = url + '/Query+View';
          break;
        case 'User-Profile':
          url = url + '/User+Profile';
          break;
        default:
          url = url + '/LMS+Workstation+Help';
          break;
      }
    }

    return url;
  }

  /**
   * Retrieve Qualifier value according to qualifier code.
   * @param {LocoDetail} loco
   * @returns
   * @memberof AppUtilityService
   */
  getQulifierValue(loco: LocoDetail) {
    if (loco && loco.qualifier) {
      if (loco.qualifier.trim().toUpperCase() === 'U') {
        return 'Idle or Yard Service';

      } else if (loco.qualifier.trim().toUpperCase() === 'I') {
        return 'Inbound';

      } else if (loco.qualifier.trim().toUpperCase() === 'E') {
        return 'Enroute';

      } else if (loco.qualifier.trim().toUpperCase() === 'O') {
        return 'Ordered to Depart';

      } else if (loco.qualifier.trim().toUpperCase() === 'D') {
        return 'Due Out of Shop';

      } else if (loco.qualifier.trim().toUpperCase() === 'S') {
        return 'In Shop';

      } else if (loco.qualifier.trim().toUpperCase() === 'W') {
        return 'In Storage';

      } else if (loco.qualifier.trim().toUpperCase() === 'Y') {
        return 'Offline';

      }
    }
    return '';
  }

  /**
   * Check for allocation station is valid or not.
   *
   * @param {string} value
   * @returns {boolean}
   */
  isAllocationStationExist(value: string): boolean {
    this.commonopsPicklist = this.validateCommonPickList(this.commonopsPicklist);
    if (value) {
      value = value.toUpperCase().trim();
      if (this.commonopsPicklist && this.commonopsPicklist.masterData) {
        this.allocationStationArr = this.commonopsPicklist.masterData.allocationStationList;
        for (const allocStation of this.allocationStationArr) {
          if (allocStation.allocationStation === value) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
 * Format a date string to only show the date part (removes time, normalizes separator)
 * @param {string} dateStr
 * @returns {string}
 */
  formatDateOnly(dateStr: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.split(' ')[0];
    if (datePart.indexOf('-') > -1) {
      return datePart.replace(/-/g, '/');
    }
    return datePart;
  }
}